/**
 * GettingStarted — "New here? Show me around" quick tour.
 *
 * A single modal that answers the first-day question:
 *   "Where do I click to do the four things this business does every day?"
 *
 * It shows four small flow cards, each spelling out the top-to-bottom
 * sequence of nav links for one core loop (sell, buy, make, review). Every
 * step that maps to a real page becomes a Link; steps that are actions on
 * an existing screen (e.g. "consume material" is a button inside a
 * Production Order) render as plain text with an em-dash hint.
 *
 * The component is intentionally stateless — no forms, no fetches. It's a
 * cheat-sheet, not a wizard. Opening/closing is fully controlled by the
 * parent (the Sidebar's "New here?" launcher).
 */
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Compass,
  Factory,
  ShoppingCart,
  Truck,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { AppModal } from '@/shared/ui/app-modal';
import { cn } from '@/shared/ui/utils';

/** One click-through step in a flow card. `to` = link, `note` = plain text. */
interface FlowStep {
  label: string;
  to?: string;
  note?: string;
}

interface Flow {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  /** Tailwind accent — used for the icon chip and the flow's numbered badge. */
  accent: string;
  steps: FlowStep[];
}

/**
 * The four core loops. Order mirrors how a shop-floor operator actually
 * uses the ERP over a normal day: sell first (front of house), then buy
 * (replenish), then make (produce), then look at the numbers.
 */
const FLOWS: Flow[] = [
  {
    title: 'Sell something',
    subtitle: 'Turn interest into cash.',
    icon: ShoppingCart,
    accent: 'text-emerald-500 bg-emerald-500/10',
    steps: [
      { label: 'Add a Lead',             to: '/crm/leads' },
      { label: 'Send a Quotation',       to: '/sales/quotations' },
      { label: 'Confirm the Sales Order', to: '/sales/orders' },
      { label: 'Raise the Sales Invoice', to: '/sales/invoices' },
      { label: 'Record the Receipt',     to: '/accounting/receipts-payments' },
    ],
  },
  {
    title: 'Buy something',
    subtitle: 'Get stock in the door.',
    icon: Truck,
    accent: 'text-sky-500 bg-sky-500/10',
    steps: [
      { label: 'Raise a Purchase Order', to: '/purchase/orders' },
      { label: 'Log the Goods Receipt',  to: '/purchase/grn' },
      { label: 'Book the Bill',          to: '/purchase/bills' },
      { label: 'Pay the supplier',       to: '/accounting/receipts-payments' },
    ],
  },
  {
    title: 'Make something',
    subtitle: 'Batch-produce a finished item.',
    icon: Factory,
    accent: 'text-violet-500 bg-violet-500/10',
    steps: [
      { label: 'Set the recipe (BOM / Formula)', to: '/manufacturing/boms' },
      { label: 'Create a Production Order',      to: '/manufacturing/production-orders' },
      { label: 'Consume material',               note: 'inside the Production Order screen' },
      { label: 'Complete each process step',     note: 'inside the Production Order screen' },
      { label: 'Check the Batch Cost Report',    to: '/reports/batch-cost' },
    ],
  },
  {
    title: 'See if you made money',
    subtitle: 'The numbers behind the day.',
    icon: TrendingUp,
    accent: 'text-amber-500 bg-amber-500/10',
    steps: [
      { label: 'Open Sales Margin',   to: '/reports/sales-margin' },
      { label: 'Then Profit & Loss',  to: '/reports/pnl' },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export interface GettingStartedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GettingStarted({ open, onOpenChange }: GettingStartedProps) {
  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Compass className="size-5 text-primary" aria-hidden />
          How to use FullDemoERP
        </span>
      }
      description="Four everyday flows. Click any step to jump straight to that page."
      size="xl"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {FLOWS.map((flow, index) => (
          <FlowCard key={flow.title} flow={flow} index={index + 1} onOpenChange={onOpenChange} />
        ))}
      </div>
      <p className="pt-1 text-xs text-muted-foreground">
        Tip: hover any item in the sidebar to see what it does. Full tour under
        Home → Help &amp; Business Flow.
      </p>
    </AppModal>
  );
}

export default GettingStarted;

/* -------------------------------------------------------------------------- */
/*  Card                                                                      */
/* -------------------------------------------------------------------------- */

interface FlowCardProps {
  flow: Flow;
  /** 1-based number rendered as a badge in the card header. */
  index: number;
  onOpenChange: (open: boolean) => void;
}

function FlowCard({ flow, index, onOpenChange }: FlowCardProps) {
  const Icon = flow.icon;
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-border bg-card p-3 shadow-sm',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'grid size-8 shrink-0 place-items-center rounded-md',
            flow.accent,
          )}
          aria-hidden
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Flow {index}
            </span>
          </div>
          <div className="truncate text-sm font-semibold text-foreground">
            {flow.title}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {flow.subtitle}
          </div>
        </div>
      </div>

      <ol className="mt-1 flex flex-col gap-1">
        {flow.steps.map((step, i) => (
          <li key={`${flow.title}:${step.label}`} className="flex items-start gap-2">
            <span
              className={cn(
                'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full',
                'bg-muted text-2xs font-semibold text-muted-foreground',
              )}
              aria-hidden
            >
              {i + 1}
            </span>
            {step.to ? (
              <Link
                to={step.to}
                onClick={() => onOpenChange(false)}
                className={cn(
                  'group inline-flex flex-1 items-center gap-1 text-sm text-foreground',
                  'rounded focus-ring hover:text-primary',
                )}
              >
                <span className="flex-1 truncate">{step.label}</span>
                <ArrowRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
              </Link>
            ) : (
              <span className="flex-1 text-sm text-muted-foreground">
                {step.label}
                {step.note ? (
                  <span className="text-xs italic"> — {step.note}</span>
                ) : null}
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
